import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, useLocation } from "react-router-dom";

import { NewTicketDialog } from "./NewTicketDialog";
import { SavedViewDialog } from "./SavedViewDialog";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { useDraftCount } from "../lib/drafts";
import { filterSearch } from "../lib/ticket-filters";
import {
  MAIN_VIEWS,
  isViewId,
  savedViewIdFrom,
  userIdFromView,
  type ViewId,
} from "../lib/views";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./TicketSearch";
import { ResizeHandle } from "./ui/ResizeHandle";
import { cn } from "./ui/cn";
import { useIsDesktop } from "../lib/media";
import { usePaneSize } from "../lib/pane-size";
import type { SavedView, User } from "../types";

/**
 * View navigation.
 *
 * Views rather than queues: agents think in terms of "what is on my plate" and
 * "what has nobody picked up", not which shared address the mail happened to
 * arrive at. The channel is still selectable, but as a filter -- it narrows a
 * view rather than being the top-level axis.
 *
 * Every agent's view is listed and none are restricted. Covering someone else's
 * tickets is routine on a support desk, and hiding them behind a permission
 * would mean asking an admin to see work you are about to pick up.
 */
export function ViewSidebar({
  user,
  connected,
}: {
  user: User;
  connected: boolean;
}) {
  const [composing, setComposing] = useState(false);
  const [editingView, setEditingView] = useState<SavedView | "new" | null>(null);

  /*
   * Every view link carries the filters currently applied.
   *
   * A `NavLink` goes to a location, and a location without them is a location
   * without them -- so filtering to a pipeline and then opening "All closed"
   * used to answer a narrower question with the whole queue. Carrying them is
   * what makes the filter bar a way of looking at the desk rather than a
   * property of one view.
   *
   * Only the filters travel, never `?q=`. A search is a question about every
   * ticket there is, already answered on screen; re-running it in a view the
   * agent has just chosen to look at instead is not what the click meant.
   */
  const carry = filterSearch(useLocation().search);

  /*
   * How many unsent drafts this browser is holding.
   *
   * Subscribed rather than recounted on navigation: the "+" dialog is opened
   * from this very sidebar, so the commonest way this number changes involves
   * no navigation at all. See lib/drafts.ts.
   */
  const drafts = useDraftCount();

  const counts = useQuery({
    queryKey: queryKeys.viewCounts,
    queryFn: api.viewCounts,
    // Cheap and constantly stale -- tickets arrive and get reassigned all day.
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  const saved = useQuery({
    queryKey: queryKeys.savedViews,
    queryFn: api.savedViews,
    staleTime: 60_000,
  });

  /*
   * Starred views, on the server rather than in this browser.
   *
   * The first attempt at this was a draggable Users list kept in
   * localStorage, and the objection to it was the right one: an arrangement
   * that does not survive a different machine is not an arrangement, it is a
   * thing you redo. A favourite is small, personal and permanent, which is
   * the same argument `dashboard-layout.ts` makes for going to the server.
   */
  const favourites = useQuery({
    queryKey: queryKeys.favourites,
    queryFn: api.favourites,
    staleTime: 60_000,
  });

  const queryClient = useQueryClient();

  /*
   * Optimistic, and it has to be. A star is a click on a control the size of a
   * full stop, and the row it belongs to jumps to another section when it
   * lands -- waiting a round trip for that means the click appears to have
   * done nothing, so it gets clicked again, which unstars it.
   */
  const star = useMutation({
    mutationFn: ({ key, on }: { key: ViewId; on: boolean }) =>
      on ? api.addFavourite(key) : api.removeFavourite(key),
    onMutate: async ({ key, on }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.favourites });
      const previous = queryClient.getQueryData<{ keys: string[] }>(
        queryKeys.favourites,
      );

      queryClient.setQueryData<{ keys: string[] }>(
        queryKeys.favourites,
        (current) => {
          const keys = current?.keys ?? [];
          return {
            keys: on
              ? keys.includes(key)
                ? keys
                : [...keys, key]
              : keys.filter((entry) => entry !== key),
          };
        },
      );

      return { previous };
    },
    /* Put it back rather than leaving a star the server never took. */
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.favourites, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.favourites });
    },
  });

  const starred = new Set(favourites.data?.keys ?? []);
  const toggleStar = (key: ViewId): void =>
    star.mutate({ key, on: !starred.has(key) });

  const countFor = (view: ViewId): number | undefined => {
    /* The one count the server does not hold: an unsent draft is in this
       browser, so this view's size is a local question. */
    if (view === "drafts") return drafts;

    const data = counts.data;
    if (!data) return undefined;
    switch (view) {
      case "unassigned":
        return data.unassigned;
      case "mine":
        return data.mine;
      case "mentions":
        return data.mentions;
      case "my-team":
        return data.myTeam;
      case "open":
        return data.open;
      case "overdue":
        return data.overdue;
      case "closed":
        return data.closed;
      case "spam":
        return data.spam;
      default:
        return undefined;
    }
  };

  // The signed-in agent first: their own row is the one they reach for most.
  const agents = [...(users.data ?? [])].sort((a, b) => {
    if (a.id === user.id) return -1;
    if (b.id === user.id) return 1;
    return a.name.localeCompare(b.name);
  });

  /*
   * Each starred key, resolved back into the row it stands for.
   *
   * A key that matches nothing is dropped rather than drawn as "Saved view" or
   * "Agent": a favourite exists to be recognised at a glance, and one that
   * cannot say what it is has stopped being one. That covers a saved view
   * somebody deleted, an agent who has been deactivated, and the moment before
   * either list has loaded -- all of which resolve again by themselves when
   * the answer arrives.
   *
   * Grouped in the order the sections below are, not in the order they were
   * starred. Starring is done over weeks and the arrival order carries no
   * meaning; the section order is one the eye has already learned from the
   * rest of the sidebar.
   */
  const favouriteViews = [...(favourites.data?.keys ?? [])]
    .filter((key): key is ViewId => isViewId(key))
    .map((key) => {
      const savedId = savedViewIdFrom(key);
      if (savedId !== null) {
        const view = (saved.data ?? []).find((entry) => entry.id === savedId);
        return view
          ? {
              key,
              label: view.name,
              badge: view.isShared ? "shared" : undefined,
              rank: 1,
            }
          : null;
      }

      const agentId = userIdFromView(key);
      if (agentId !== null) {
        const agent = agents.find((entry) => entry.id === agentId);
        return agent
          ? {
              key,
              label:
                agent.id === user.id ? `${agent.name} (you)` : agent.name,
              emoji: agent.emoji,
              count: counts.data?.byAssignee[agent.id] ?? 0,
              rank: 2,
            }
          : null;
      }

      const preset = MAIN_VIEWS.find((entry) => entry.id === key);
      return preset
        ? {
            key,
            label: preset.label,
            count: countFor(key),
            highlight:
              key === "mentions" && (counts.data?.mentionsUnread ?? 0) > 0,
            rank: 0,
          }
        : null;
    })
    .filter((entry) => entry !== null)
    .sort((a, b) => a.rank - b.rank);

  /*
   * How wide the navigation is. Worth dragging for the same reason the
   * favourites section exists: these rows carry people's names and saved view
   * names, and both are as long as somebody decided they were.
   */
  const isDesktop = useIsDesktop();
  const width = usePaneSize("pulse.pane.sidebar", 240, { min: 176, max: 420 });

  return (
    <div className="flex min-h-0 shrink-0">
      <nav
        /*
         * The dragged width, on a desktop only. On a phone this same sidebar
         * is the off-canvas drawer, where a width somebody chose against a
         * monitor would open as a panel with no room left for the scrim to
         * dismiss it -- so the drawer keeps the class-based `w-60`.
         */
        style={isDesktop ? { width: width.size } : undefined}
        className={cn(
          "flex shrink-0 flex-col overflow-y-auto bg-app pb-3",
          !isDesktop && "w-60",
        )}
      >
        <div className="flex items-center gap-2 px-4 py-4">
          {/* The product, not the platform. Pulse is the thing in the app rail
              that holds this and the CRM; this sidebar belongs to the Helpdesk. */}
          <span className="text-sm font-semibold text-content">Helpdesk</span>
          {/*
            Connection dot. When the socket drops, updates silently stop arriving
            — showing that state is what stops an agent trusting a stale screen.
          */}
          <span
            title={connected ? "Live updates connected" : "Reconnecting…"}
            className={cn(
              "size-2 shrink-0 rounded-full",
              connected ? "bg-sla-ok" : "bg-sla-warning",
            )}
          />

          {/*
            Raising a ticket has to be reachable from anywhere -- an agent on the
            phone needs it without first navigating somewhere that offers it.
          */}
          <span className="ml-auto">
            <NotificationBell />
          </span>

          <button
            type="button"
            onClick={() => setComposing(true)}
            title="New ticket"
            aria-label="New ticket"
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
              "bg-brand-600 text-white transition-colors hover:bg-brand-700",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          >
            +
          </button>
        </div>

        {composing ? (
          <NewTicketDialog onClose={() => setComposing(false)} />
        ) : null}

        {editingView ? (
          <SavedViewDialog
            key={editingView === "new" ? "new" : editingView.id}
            view={editingView === "new" ? null : editingView}
            user={user}
            onClose={() => setEditingView(null)}
          />
        ) : null}

        {/*
          Above everything it could take you to, because it is a way to leave any
          of them. It sits with the bell and the new-ticket button rather than in
          the Views list -- it is not a view, and a search that only worked once
          you were already looking at tickets was the thing worth fixing.
        */}
        <div className="px-4 pb-3">
          <GlobalSearch />
        </div>

        {/* Above Views: the dashboard answers "where is the desk right now",
            which is the question you ask before choosing a queue. Calls sits
            with it rather than among the views because it is not a queue of
            tickets -- it is the phone system's history beside the desk's. */}
        <ul className="mb-3 px-2">
          <ViewLink to="/dashboard" label="Dashboard" />
          <ViewLink to="/calls" label="Calls" />
        </ul>

        {/*
          Its own section rather than another line under Dashboard: the audit is
          a weekly review of individual work, done by a manager for a different
          reason than the daily figures above. Only admins have the route, so
          offering the link to anyone else would lead to a redirect.
        */}
        {user.role === "admin" ? (
          <>
            <div className="px-4 pb-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-faint">
                Manage
              </span>
            </div>
            <ul className="mb-3 px-2">
              <ViewLink to="/audit" label="Team audit" />
            </ul>
          </>
        ) : null}

        <div className="flex items-center gap-1 px-4 pb-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-faint">
            Views
          </span>
          <button
            type="button"
            onClick={() => setEditingView("new")}
            title="New view"
            aria-label="New view"
            className="ml-auto flex h-4 w-4 items-center justify-center rounded text-faint hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
          >
            +
          </button>
        </div>
        {/*
          Drafts is always here, empty or not.

          It used to appear only once there was something in it, on the
          argument that a permanent "Drafts 0" is a row never worth clicking.
          That argument was wrong about what a folder is for. Somebody who has
          written half a reply goes looking for where it went, and a row that
          is absent exactly when they have not yet written one teaches them it
          does not exist -- so when it does appear, they have already stopped
          looking. The count still says nought, which is a fact about the
          folder rather than a reason to hide it.
        */}
        <ul className="px-2">
          {MAIN_VIEWS.map((view) => (
            <ViewLink
              key={view.id}
              to={`/v/${view.id}${carry}`}
              label={view.label}
              count={countFor(view.id)}
              /*
               * The number counts every ticket in the view, so it always agrees
               * with the list it opens. The colour carries the other half --
               * whether any of them are still unread.
               */
              highlight={
                view.id === "mentions" && (counts.data?.mentionsUnread ?? 0) > 0
              }
              starred={starred.has(view.id)}
              onToggleStar={() => toggleStar(view.id)}
            />
          ))}

        </ul>

        {/* Saved views, directly under the presets they extend. */}
        {(saved.data ?? []).length > 0 ? (
          <>
            <SectionLabel className="mt-4">My views</SectionLabel>
            <ul className="px-2">
              {(saved.data ?? []).map((view) => (
                <li key={view.id} className="group/view relative">
                  <ViewLink
                    to={`/v/saved-${view.id}${carry}`}
                    label={view.name}
                    // A shared view is marked, so nobody edits one the whole desk
                    // relies on thinking it is only theirs.
                    badge={view.isShared ? "shared" : undefined}
                    starred={starred.has(`saved-${view.id}`)}
                    onToggleStar={() => toggleStar(`saved-${view.id}`)}
                    /* Room for the pencil, which sits where the star would. */
                    starOffset
                  />
                  {view.ownerUserId === user.id || user.role === "admin" ? (
                    <button
                      type="button"
                      onClick={() => setEditingView(view)}
                      title={`Edit ${view.name}`}
                      aria-label={`Edit ${view.name}`}
                      className="absolute right-1 top-1.5 hidden h-5 w-5 items-center justify-center rounded text-faint hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400 group-hover/view:flex"
                    >
                      ✎
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {/*
          Starred views, above Users and below the lists they came from.
        
          Open by default, unlike Users. A section somebody built by hand is one
          they want in front of them -- and it is only as long as they made it,
          so it cannot be the wall of names that made Users worth collapsing.
          Hidden entirely until there is something in it: an empty section with a
          disclosure triangle is a control that does nothing, and explaining
          stars to somebody who has not made one is the tooltip's job.
        */}
        {favouriteViews.length > 0 ? (
          <details open className="group mt-4">
            <summary className="flex cursor-pointer list-none items-center gap-1 px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint hover:text-muted">
              <span aria-hidden className="transition-transform group-open:rotate-90">
                ▶
              </span>
              Favourites
            </summary>
            <ul className="px-2">
              {favouriteViews.map((entry) => (
                <ViewLink
                  key={entry.key}
                  to={`/v/${entry.key}${carry}`}
                  label={entry.label}
                  emoji={entry.emoji}
                  count={entry.count}
                  badge={entry.badge}
                  highlight={entry.highlight}
                  starred
                  onToggleStar={() => toggleStar(entry.key)}
                />
              ))}
            </ul>
          </details>
        ) : null}

        {/* Collapsed by default: a full desk is a long list, and it is not what
            anybody is looking at when they open the app. */}
        <details className="group mt-4">
          <summary className="flex cursor-pointer list-none items-center gap-1 px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint hover:text-muted">
            <span aria-hidden className="transition-transform group-open:rotate-90">
              ▶
            </span>
            Users
          </summary>
          <ul className="px-2">
            {agents.map((agent) => (
              <ViewLink
                key={agent.id}
                to={`/v/user-${agent.id}${carry}`}
                label={agent.id === user.id ? `${agent.name} (you)` : agent.name}
                emoji={agent.emoji}
                count={counts.data?.byAssignee[agent.id] ?? 0}
                starred={starred.has(`user-${agent.id}`)}
                onToggleStar={() => toggleStar(`user-${agent.id}`)}
              />
            ))}
          </ul>
        </details>

        <ul className="mt-4 border-t border-line px-2 pt-2">
          <ViewLink to="/canned" label="Canned responses" />
          {/* Routing rules are a manager's tool; an agent has nothing to do
              here and the endpoint refuses them anyway. */}
          {user.role === "admin" ? (
            <ViewLink to="/automations" label="Automations" />
          ) : null}
        </ul>
      </nav>

      {/* The sidebar's right border, made draggable. Desktop only -- there is
          nothing to drag in a drawer that is dismissed by tapping beside it. */}
      {isDesktop ? (
        <ResizeHandle
          axis="x"
          label="Sidebar"
          size={width.size}
          min={176}
          max={420}
          onChange={width.setSize}
          onReset={width.reset}
        />
      ) : null}
    </div>
  );
}

function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-4 pb-1 text-[11px] font-medium uppercase tracking-wide text-faint",
        className,
      )}
    >
      {children}
    </div>
  );
}

function ViewLink({
  to,
  label,
  emoji,
  count,
  highlight,
  badge,
  starred,
  onToggleStar,
  starOffset = false,
}: {
  to: string;
  label: string;
  /** The agent's own glyph, on the rows that stand for a person. */
  emoji?: string | null;
  count?: number;
  highlight?: boolean;
  badge?: string;
  starred?: boolean;
  /** Omitted on the rows that are not views -- Dashboard, Canned responses. */
  onToggleStar?: () => void;
  /** Shift the star left, for a row that already has a control on the right. */
  starOffset?: boolean;
}) {
  return (
    <li className="group/row relative">
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
            isActive
              ? "bg-surface-2 font-medium text-content"
              : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
            /* Room for the star, always -- reserved rather than made on
               hover, so a row does not reflow under the pointer that is
               about to click it. */
            onToggleStar && (starOffset ? "pr-12" : "pr-7"),
          )
        }
      >
        {emoji !== undefined ? (
          /*
           * Reserved on every row that stands for a person, filled only on the
           * ones whose person has picked something -- which is why this tests
           * for the prop rather than for a glyph. Half a desk will never set
           * one, and a column of names that start in two different places
           * costs more scanning than the glyphs save, which is the whole point
           * of them being here.
           *
           * `aria-hidden`: the name beside it already says who this is, and a
           * screen reader announcing "fern, Priya Shah" is reading out a
           * decoration as though it were information.
           */
          <span aria-hidden className="w-4 shrink-0 text-center leading-none">
            {emoji}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {badge ? (
          <span className="shrink-0 rounded bg-surface-2 px-1 text-[9px] uppercase text-faint">
            {badge}
          </span>
        ) : null}
        {count !== undefined ? (
          <span
            className={cn(
              "shrink-0 tabular-nums",
              highlight
                ? "rounded-full bg-brand-600 px-1.5 text-[10px] font-medium text-white"
                : "text-[11px] text-faint",
            )}
          >
            {count}
          </span>
        ) : null}
      </NavLink>

      {/*
        The star sits over the link rather than inside it. A button inside an
        anchor is interactive content inside interactive content, which is
        invalid and behaves differently in every screen reader -- the same
        reason TicketCard has no nested click targets.

        Shown on hover, and always once it is on. A starred row has to say so
        while the pointer is elsewhere, or the section above stops being
        explicable; an unstarred one does not need a control on it until
        somebody is looking at that row.
      */}
      {onToggleStar ? (
        <button
          type="button"
          onClick={onToggleStar}
          title={starred ? `Remove ${label} from favourites` : `Add ${label} to favourites`}
          aria-label={
            starred ? `Remove ${label} from favourites` : `Add ${label} to favourites`
          }
          aria-pressed={starred}
          className={cn(
            "absolute top-1.5 flex h-5 w-5 items-center justify-center rounded text-xs leading-none",
            starOffset ? "right-6" : "right-1",
            "hover:bg-surface",
            starred
              ? "text-amber-500"
              : "hidden text-faint hover:text-amber-500 group-hover/row:flex focus-visible:flex",
          )}
        >
          {starred ? "★" : "☆"}
        </button>
      ) : null}
    </li>
  );
}
